'use client';

import { useMemo } from 'react';
import { useAdminDailyUserMatrix } from '../_lib/hooks';

interface Props {
  period: string;
}

/**
 * Date × User 矩阵热力图：横轴日期，纵轴员工，格子色深度按 tokens 归一化。
 * 仅 admin 用，需要 ai-usage:view-all 权限。
 */
export function DailyUserHeatmap({ period }: Props) {
  const { data, isFetching } = useAdminDailyUserMatrix(period);
  const loading = isFetching && !data;

  const { lookup, maxTokens } = useMemo(() => {
    if (!data) return { lookup: new Map(), maxTokens: 0 };
    const m = new Map<string, { tokens: number; cost: string }>();
    let max = 0;
    for (const c of data.cells) {
      m.set(`${c.date}|${c.userId}`, { tokens: c.tokens, cost: c.costUsd });
      if (c.tokens > max) max = c.tokens;
    }
    return { lookup: m, maxTokens: max };
  }, [data]);

  if (loading) {
    return (
      <div className="bg-white rounded shadow p-4 mb-4">
        <div className="text-xs text-gray-400">Loading heatmap…</div>
      </div>
    );
  }
  if (!data || data.users.length === 0) {
    return (
      <div className="bg-white rounded shadow p-4 mb-4">
        <div className="text-sm font-medium text-gray-700 mb-2">员工 × 日期 热力图</div>
        <div className="text-xs text-gray-400">无数据</div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded shadow p-4 mb-4 overflow-x-auto">
      <div className="text-sm font-medium text-gray-700 mb-3">员工 × 日期 热力图（Token，Top 10 员工）</div>
      <table className="text-[10px] border-collapse">
        <thead>
          <tr>
            <th className="px-2 py-1 text-left sticky left-0 bg-white">员工</th>
            {data.dates.map((d) => (
              <th key={d} className="px-1 py-1 text-center font-normal text-gray-500" title={d}>
                {d.slice(5)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.users.map((u) => (
            <tr key={u.id}>
              <td className="px-2 py-0.5 whitespace-nowrap sticky left-0 bg-white">{u.label}</td>
              {data.dates.map((d) => {
                const cell = lookup.get(`${d}|${u.id}`);
                const intensity = cell && maxTokens > 0 ? cell.tokens / maxTokens : 0;
                const bg = intensityToColor(intensity);
                return (
                  <td
                    key={d}
                    className="border border-white"
                    style={{ width: 18, height: 18, backgroundColor: bg }}
                    title={cell ? `${u.label} · ${d}\n${cell.tokens.toLocaleString()} tokens · $${Number(cell.cost).toFixed(3)}` : `${u.label} · ${d} · 无活动`}
                  />
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
      <div className="mt-3 flex items-center gap-2 text-[10px] text-gray-500">
        <span>低</span>
        {[0.1, 0.3, 0.5, 0.7, 0.9].map((v) => (
          <span key={v} style={{ background: intensityToColor(v), width: 18, height: 14, display: 'inline-block' }} />
        ))}
        <span>高</span>
        <span className="ml-3">max: {maxTokens.toLocaleString()} tokens/day</span>
      </div>
    </div>
  );
}

function intensityToColor(v: number): string {
  if (v <= 0) return '#f3f4f6';
  // 蓝色渐变 lightness 95% → 30%
  const lightness = Math.round(95 - v * 65);
  return `hsl(213, 80%, ${lightness}%)`;
}
